import { createRoot } from 'react-dom/client';
import React from 'react';
import AutoSuggestionPopup from './AutoSuggestionPopup';
import "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/theme-textmate";
import "./AutoSuggestion.css";
import { eplStyles } from "./AllStyles"

export function getPositionFromIndex(text, startIndex, length) {
    let row = 0;
    let col = 0;

    for (let i = 0; i < startIndex; i++) {
        if (text[i] === '\n') {
            row++;
            col = 0;
        } else {
            col++;
        }
    }

    const startRow = row;
    const startCol = col;

    for (let i = startIndex; i < startIndex + length; i++) {
        if (text[i] === '\n') {
            row++;
            col = 0;
        } else {
            col++;
        }
    }

    return { startRow, startCol, endRow: row, endCol: col };
}

// Create anchor and add to list
export function createAnchor(session, Range, position, originalText, suggestionText, markerClass, styleName, description, anchorlist) {
    const startAnchor = session.doc.createAnchor(position.startRow, position.startCol);
    const endAnchor = session.doc.createAnchor(position.endRow, position.endCol);

    startAnchor.$insertRight = false;
    endAnchor.$insertRight = true;

    const range = new Range(position.startRow, position.startCol, position.endRow, position.endCol);
    const markerId = session.addMarker(range, markerClass, "background");
    
    anchorlist.push({
        startAnchor,
        endAnchor,
        markerId,
        originalText,
        suggestionText,
        range,
        styleInfo: {
            name: styleName,
            description: description
        }
    });
}

// Escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}


// ==================== MAIN AUTO SUGGESTION LOGIC ====================

let globalAnchorList = [];
let activeStyles = [];

export async function AutoSuggestion(texFile, ace, selectedStyles = [], props, setTextFile) {
    console.log("Selected styles:", selectedStyles);
    if (!ace?.current) return null;

    clearPreviousMarkers(ace);
    console.log("Selected styles:", selectedStyles);
    activeStyles = selectedStyles
        .map(styleName => eplStyles[styleName])
        .filter(Boolean);

    if (activeStyles.length === 0) {
        console.warn("No valid styles selected");
        return [];
    }

    let anchorlist = [];

    try {
        const editor = ace.current.editor;
        const session = editor.session;
        const Range = ace.current.editor.Range || window.ace.require('ace/range').Range;

        // Small delay to ensure editor is fully ready/updated (Fixes race condition with react-ace)
        await new Promise(resolve => setTimeout(resolve, 100));

        removePopup();

        // Execute each style's logic (handle async)
        for (const style of activeStyles) {
            if (typeof style.execute === 'function') {
                // Check if execute is async
                const result = style.execute(texFile, session, Range, anchorlist);
                if (result instanceof Promise) {
                    await result; // Wait for async completion
                }
            }
        }

        // Rest of the code remains the same...
        try {
            props.uploadAutoSuggestions(anchorlist);
        } catch (err) {
            console.log("Error uploading suggestions:", err);
        }

        globalAnchorList = anchorlist;
        console.log("Created anchors:", anchorlist);

        // Force render
        session._signal("changeBackMarker");
        editor.renderer.updateBackMarkers();
        editor.renderer.updateFull(true);

        if (!editor._initialMarkerRenderDone) {
            editor._initialMarkerRenderDone = true;
        }

        if (!editor.container._hoverListenerSetup) {
            setupHoverListener(editor, setTextFile);
            editor.container._hoverListenerSetup = true;
        }

    } catch (error) {
        console.error("AutoSuggestion error:", error);
    }

    return anchorlist;
}


function setupHoverListener(editor, setTextFile, props) {
    const container = editor.container;
    let currentPopup = null;
    let currentHoveredItem = null;
    let isMouseOverPopup = false;
    let hidePopupTimeout = null;

    const hoverListener = (e) => {
        if (hidePopupTimeout) {
            clearTimeout(hidePopupTimeout);
            hidePopupTimeout = null;
        }

        if (isMouseOverPopup) return;

        const position = editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
        const hoveredItem = findHoveredItem(position, globalAnchorList, props);

        if (hoveredItem) {
            const isOverActualText = position.column >= hoveredItem.startAnchor.column &&
                position.column < hoveredItem.endAnchor.column;

            if (isOverActualText && hoveredItem !== currentHoveredItem) {
                currentHoveredItem = hoveredItem;
                if (currentPopup) removePopup();

                currentPopup = showPopup(e, editor, hoveredItem, setTextFile, () => {
                    isMouseOverPopup = false;
                    currentHoveredItem = null;
                }, (value) => {
                    isMouseOverPopup = value;
                });
            }
        } else if (!hoveredItem && currentHoveredItem && !isMouseOverPopup) {
            hidePopupTimeout = setTimeout(() => {
                if (!isMouseOverPopup) {
                    currentHoveredItem = null;
                    removePopup();
                    currentPopup = null;
                }
            }, 150);
        }
    };

    container.addEventListener('mousemove', hoverListener);
    container._hoverListener = hoverListener;
}

function findHoveredItem(position, anchorlist, props) {
    if (!anchorlist || anchorlist.length === 0) return null;

    return anchorlist.find(item => {
        const startRow = item.startAnchor.row;
        const startCol = item.startAnchor.column;
        const endRow = item.endAnchor.row;
        const endCol = item.endAnchor.column;

        if (position.row < startRow || position.row > endRow) return false;
        if (startRow === endRow) {
            return position.column >= startCol && position.column <= endCol;
        }
        if (position.row === startRow) {
            return position.column >= startCol;
        } else if (position.row === endRow) {
            return position.column <= endCol;
        } else {
            return true;
        }
    });
}

function showPopup(mouseEvent, editor, item, setTextFile, onPopupClose, setMouseOverPopup) {
    removePopup();

    const popupContainer = document.createElement('div');
    popupContainer.className = 'ace-suggestion-popup-container'; // Changed class name
    popupContainer.id = 'ace-suggestion-popup';

    // Calculate position - BELOW the text, CENTERED
    const renderer = editor.renderer;
    // Get screen coordinates for both start and end of the marker range to find center
    const startScreenPos = renderer.textToScreenCoordinates(item.startAnchor.row, item.startAnchor.column);
    const endScreenPos = renderer.textToScreenCoordinates(item.endAnchor.row, item.endAnchor.column);
    const editorRect = editor.container.getBoundingClientRect();
    
    // Calculate center horizontal position relative to editor
    // We use the start and end of the selection to find the horizontal center
    const centerX = (startScreenPos.pageX + endScreenPos.pageX) / 2;
    
    // Default position relative to editor container
    let top = endScreenPos.pageY - editorRect.top + renderer.lineHeight; // Add line height to put it below
    
    // Assuming popup width is roughly 300px (min-width is 250px in css), center it
    let left = centerX - editorRect.left - 150; 

    // Boundary text (rudimentary) - prevent going off screen
    if (left < 10) left = 10;
    if (left + 300 > editorRect.width) {
        left = editorRect.width - 310; 
    }

    popupContainer.style.position = 'absolute';
    popupContainer.style.left = `${left}px`;
    popupContainer.style.top = `${top}px`;
    popupContainer.style.zIndex = '1000';

    editor.container.appendChild(popupContainer);

    // React Render
    const root = createRoot(popupContainer);
    root.render(
        <AutoSuggestionPopup
            item={item}
            onAccept={() => {
                acceptSuggestion(editor, item, setTextFile);
                handleClose(popupContainer, root, setMouseOverPopup, onPopupClose);
            }}
            onReject={() => {
                handleClose(popupContainer, root, setMouseOverPopup, onPopupClose);
            }}
        />
    );

    // Event listeners
    popupContainer.addEventListener('mouseenter', () => setMouseOverPopup(true));
    popupContainer.addEventListener('mouseleave', () => {
        setMouseOverPopup(false);
        setTimeout(() => {
             // Check if still mouse over (React update might lag slightly) - logic handled by hover listener mostly
             handleClose(popupContainer, root, setMouseOverPopup, onPopupClose);
        }, 300); // 300ms delay
    });

    return popupContainer;
}

function handleClose(container, root, setMouseOverPopup, onPopupClose) {
    // Only close if it exists
    if (document.getElementById('ace-suggestion-popup') === container) {
        setMouseOverPopup(false);
        root.unmount();
        container.remove();
        if (onPopupClose) onPopupClose();
    }
}

function acceptSuggestion(editor, item, setTextFile) {
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;

    const range = new Range(
        item.startAnchor.row,
        item.startAnchor.column,
        item.endAnchor.row,
        item.endAnchor.column
    );

    session.replace(range, item.suggestionText);
    session.removeMarker(item.markerId);
    item.startAnchor.detach();
    item.endAnchor.detach();

    const index = globalAnchorList.indexOf(item);
    if (index > -1) {
        globalAnchorList.splice(index, 1);
    }

    if (setTextFile) {
        setTextFile(session.getValue());
    }

    console.log(`[${item.styleInfo.name}] Accepted: ${item.originalText} → ${item.suggestionText}`);
}

function removePopup() {
    const existingPopup = document.getElementById('ace-suggestion-popup');
    if (existingPopup) {
        existingPopup.remove();
    }
}

function clearPreviousMarkers(ace) {
    if (!ace?.current || globalAnchorList.length === 0) return;

    const session = ace.current.editor.session;

    globalAnchorList.forEach(item => {
        if (item.markerId) session.removeMarker(item.markerId);
        if (item.startAnchor) item.startAnchor.detach();
        if (item.endAnchor) item.endAnchor.detach();
    });

    globalAnchorList = [];
}

export function clearAutoSuggestions(ace) {
    if (!ace?.current) return;

    const session = ace.current.editor.session;
    const container = ace.current.editor.container;

    if (container._hoverListener) {
        container.removeEventListener('mousemove', container._hoverListener);
        delete container._hoverListener;
        delete container._hoverListenerSetup;
    }

    removePopup();

    globalAnchorList.forEach(item => {
        if (item.markerId) session.removeMarker(item.markerId);
        if (item.startAnchor) item.startAnchor.detach();
        if (item.endAnchor) item.endAnchor.detach();
    });

    globalAnchorList = [];
    activeStyles = [];
}

export function getAvailableStyles() {
    return Object.keys(eplStyles).map(key => ({
        id: key,
        name: eplStyles[key].name,
        description: eplStyles[key].description
    }));
}











// import "ace-builds/src-noconflict/ace";
// import "ace-builds/src-noconflict/theme-textmate";
// import "./AutoSuggestion.css";
// import { eplStyles } from "./AllStyles"

// export function getPositionFromIndex(text, startIndex, length) {
//     let row = 0;
//     let col = 0;

//     for (let i = 0; i < startIndex; i++) {
//         if (text[i] === '\n') {
//             row++;
//             col = 0;
//         } else {
//             col++;
//         }
//     }

//     const startRow = row;
//     const startCol = col;

//     for (let i = startIndex; i < startIndex + length; i++) {
//         if (text[i] === '\n') {
//             row++;
//             col = 0;
//         } else {
//             col++;
//         }
//     }

//     return { startRow, startCol, endRow: row, endCol: col };
// }

// // Create anchor and add to list
// export function createAnchor(session, Range, position, originalText, suggestionText, markerClass, styleName, description, anchorlist) {
//     const startAnchor = session.doc.createAnchor(position.startRow, position.startCol);
//     const endAnchor = session.doc.createAnchor(position.endRow, position.endCol);

//     startAnchor.$insertRight = false;
//     endAnchor.$insertRight = true;

//     const range = new Range(position.startRow, position.startCol, position.endRow, position.endCol);
//     const markerId = session.addMarker(range, markerClass, "background");

//     anchorlist.push({
//         startAnchor,
//         endAnchor,
//         markerId,
//         originalText,
//         suggestionText,
//         range,
//         styleInfo: {
//             name: styleName,
//             description: description
//         }
//     });
// }

// // Escape HTML
// function escapeHtml(text) {
//     const div = document.createElement('div');
//     div.textContent = text;
//     return div.innerHTML;
// }


// // ==================== MAIN AUTO SUGGESTION LOGIC ====================

// let activeStyles = [];
// let editorInstance = null;
// let setTextFileCallback = null;

// export async function AutoSuggestion(texFile, ace, selectedStyles = [], props, setTextFile) {
//     console.log("Selected styles:", selectedStyles);
//     if (!ace?.current) return null;

//     // Store editor reference and callback for popup usage
//     editorInstance = ace.current.editor;
//     setTextFileCallback = setTextFile;

//     clearPreviousMarkers(ace, props);

//     activeStyles = selectedStyles
//         .map(styleName => eplStyles[styleName])
//         .filter(Boolean);

//     if (activeStyles.length === 0) {
//         console.warn("No valid styles selected");
//         return [];
//     }

//     let anchorlist = [];

//     try {
//         const editor = ace.current.editor;
//         const session = editor.session;
//         const Range = ace.current.editor.Range || window.ace.require('ace/range').Range;

//         removePopup();

//         // Execute each style's logic (handle async)
//         for (const style of activeStyles) {
//             if (typeof style.execute === 'function') {
//                 const result = style.execute(texFile, session, Range, anchorlist);
//                 if (result instanceof Promise) {
//                     await result;
//                 }
//             }
//         }

//         // Upload to Redux
//         try {
//             props.uploadAutoSuggestions(anchorlist);
//         } catch (err) {
//             console.log("Error uploading suggestions:", err);
//         }

//         console.log("Created anchors:", anchorlist);

//         // Force render
//         session._signal("changeBackMarker");
//         editor.renderer.updateBackMarkers();

//         if (!editor._initialMarkerRenderDone) {
//             editor._initialMarkerRenderDone = true;
//             session._signal("changeBackMarker");
//             editor.renderer.updateBackMarkers();
//             editor.renderer.updateFrontMarkers();
//             editor.renderer.updateFull();
//         }

//         // Setup hover listener with props reference
//         if (!editor.container._hoverListenerSetup) {
//             setupHoverListener(editor, setTextFile, props);
//             editor.container._hoverListenerSetup = true;
//         }

//     } catch (error) {
//         console.error("AutoSuggestion error:", error);
//     }

//     return anchorlist;
// }


// function setupHoverListener(editor, setTextFile, props) {
//     const container = editor.container;
//     let currentPopup = null;
//     let currentHoveredItem = null;
//     let isMouseOverPopup = false;
//     let hidePopupTimeout = null;

//     const hoverListener = (e) => {
//         if (hidePopupTimeout) {
//             clearTimeout(hidePopupTimeout);
//             hidePopupTimeout = null;
//         }

//         if (isMouseOverPopup) return;

//         const position = editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
        
//         // Use props.suggestion instead of globalAnchorList
//         const suggestions = props.suggestion || [];
//         const hoveredItem = findHoveredItem(position, suggestions);

//         if (hoveredItem) {
//             const isOverActualText = position.column >= hoveredItem.startAnchor.column &&
//                 position.column < hoveredItem.endAnchor.column;

//             if (isOverActualText && hoveredItem !== currentHoveredItem) {
//                 currentHoveredItem = hoveredItem;
//                 if (currentPopup) removePopup();

//                 currentPopup = showPopup(e, editor, hoveredItem, setTextFile, () => {
//                     isMouseOverPopup = false;
//                     currentHoveredItem = null;
//                 }, (value) => {
//                     isMouseOverPopup = value;
//                 }, props);
//             }
//         } else if (!hoveredItem && currentHoveredItem && !isMouseOverPopup) {
//             hidePopupTimeout = setTimeout(() => {
//                 if (!isMouseOverPopup) {
//                     currentHoveredItem = null;
//                     removePopup();
//                     currentPopup = null;
//                 }
//             }, 150);
//         }
//     };

//     container.addEventListener('mousemove', hoverListener);
//     container._hoverListener = hoverListener;
// }

// function findHoveredItem(position, anchorlist) {
//     if (!anchorlist || anchorlist.length === 0) return null;

//     return anchorlist.find(item => {
//         const startRow = item.startAnchor.row;
//         const startCol = item.startAnchor.column;
//         const endRow = item.endAnchor.row;
//         const endCol = item.endAnchor.column;

//         if (position.row < startRow || position.row > endRow) return false;
//         if (startRow === endRow) {
//             return position.column >= startCol && position.column <= endCol;
//         }
//         if (position.row === startRow) {
//             return position.column >= startCol;
//         } else if (position.row === endRow) {
//             return position.column <= endCol;
//         } else {
//             return true;
//         }
//     });
// }

// function showPopup(mouseEvent, editor, item, setTextFile, onPopupClose, setMouseOverPopup, props) {
//     removePopup();

//     const popup = document.createElement('div');
//     popup.className = 'ace-suggestion-popup';
//     popup.id = 'ace-suggestion-popup';

//     popup.innerHTML = `
//         <div class="popup-content">
//             <div class="popup-header">${item.styleInfo.description}</div>
//             <div class="popup-suggestion">
//                 <span class="original">${escapeHtml(item.originalText)}</span>
//                 <span class="arrow">→</span>
//                 <span class="formatted">${escapeHtml(item.suggestionText)}</span>
//             </div>
//             <div class="popup-actions">
//                 <button class="popup-btn accept">Accept</button>
//                 <button class="popup-btn reject">Reject</button>
//             </div>
//         </div>
//     `;

//     const editorRect = editor.container.getBoundingClientRect();
//     popup.style.left = `${mouseEvent.clientX - editorRect.left + 10}px`;
//     popup.style.top = `${mouseEvent.clientY - editorRect.top - 50}px`;

//     editor.container.appendChild(popup);

//     popup.addEventListener('mouseenter', () => setMouseOverPopup(true));
//     popup.addEventListener('mouseleave', () => {
//         setMouseOverPopup(false);
//         setTimeout(() => {
//             removePopup();
//             if (onPopupClose) onPopupClose();
//         }, 100);
//     });

//     const acceptBtn = popup.querySelector('.accept');
//     const rejectBtn = popup.querySelector('.reject');

//     acceptBtn.addEventListener('click', () => {
//         acceptSuggestion(editor, item, setTextFile, props);
//         removePopup();
//         setMouseOverPopup(false);
//         if (onPopupClose) onPopupClose();
//     });

//     rejectBtn.addEventListener('click', () => {
//         rejectSuggestion(item, props);
//         removePopup();
//         setMouseOverPopup(false);
//         if (onPopupClose) onPopupClose();
//     });

//     return popup;
// }

// function acceptSuggestion(editor, item, setTextFile, props) {
//     const session = editor.session;
//     const Range = window.ace.require('ace/range').Range;

//     const range = new Range(
//         item.startAnchor.row,
//         item.startAnchor.column,
//         item.endAnchor.row,
//         item.endAnchor.column
//     );

//     session.replace(range, item.suggestionText);
//     session.removeMarker(item.markerId);
//     item.startAnchor.detach();
//     item.endAnchor.detach();

//     // Remove from Redux store
//     if (props.removeAutoSuggestion) {
//         props.removeAutoSuggestion(item);
//     }

//     if (setTextFile) {
//         setTextFile(session.getValue());
//     }

//     console.log(`[${item.styleInfo.name}] Accepted: ${item.originalText} → ${item.suggestionText}`);
// }

// function rejectSuggestion(item, props) {
//     const session = editorInstance.session;

//     // Remove marker
//     if (item.markerId) {
//         session.removeMarker(item.markerId);
//     }

//     // Detach anchors
//     if (item.startAnchor) item.startAnchor.detach();
//     if (item.endAnchor) item.endAnchor.detach();

//     // Add to rejected list
//     if (props.addAutoSuggestionReject) {
//         props.addAutoSuggestionReject({
//             row: item.startAnchor.row,
//             column: item.startAnchor.column,
//             originalText: item.originalText,
//             styleName: item.styleInfo?.name
//         });
//     }

//     // Remove from suggestions
//     if (props.removeAutoSuggestion) {
//         props.removeAutoSuggestion(item);
//     }

//     console.log(`[${item.styleInfo.name}] Rejected: ${item.originalText}`);
// }

// function removePopup() {
//     const existingPopup = document.getElementById('ace-suggestion-popup');
//     if (existingPopup) {
//         existingPopup.remove();
//     }
// }

// function clearPreviousMarkers(ace, props) {
//     if (!ace?.current) return;

//     const session = ace.current.editor.session;
//     const suggestions = props.suggestion || [];

//     // Clear markers from current suggestions in Redux
//     suggestions.forEach(item => {
//         if (item.markerId) session.removeMarker(item.markerId);
//         if (item.startAnchor) item.startAnchor.detach();
//         if (item.endAnchor) item.endAnchor.detach();
//     });
// }

// export function clearAutoSuggestions(ace, props) {
//     if (!ace?.current) return;

//     const session = ace.current.editor.session;
//     const container = ace.current.editor.container;

//     if (container._hoverListener) {
//         container.removeEventListener('mousemove', container._hoverListener);
//         delete container._hoverListener;
//         delete container._hoverListenerSetup;
//     }

//     removePopup();

//     const suggestions = props?.suggestion || [];
//     suggestions.forEach(item => {
//         if (item.markerId) session.removeMarker(item.markerId);
//         if (item.startAnchor) item.startAnchor.detach();
//         if (item.endAnchor) item.endAnchor.detach();
//     });

//     activeStyles = [];
// }

// export function getAvailableStyles() {
//     return Object.keys(eplStyles).map(key => ({
//         id: key,
//         name: eplStyles[key].name,
//         description: eplStyles[key].description
//     }));
// }